"use client";
import React, { useState, useRef, useEffect } from "react";
import { Flex, Heading, Input, IconButton, useToast } from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";

import { phoneNumberService } from "../../services/phoneNumberService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

interface EditablePhoneNumberNameProps {
  phoneNumber: string;
  nickname: string | null;
  prettyNumber: string;
  onUpdate: () => void;
}

export const EditablePhoneNumberName: React.FC<
  EditablePhoneNumberNameProps
> = ({ phoneNumber, nickname, prettyNumber, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentNickname, setCurrentNickname] = useState(
    nickname || prettyNumber || phoneNumber
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    setCurrentNickname(nickname || prettyNumber || phoneNumber);
  }, [nickname, prettyNumber, phoneNumber]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmedNickname = currentNickname.trim();

    if (!trimmedNickname) {
      setCurrentNickname(prettyNumber || phoneNumber);
      setIsEditing(false);
      return;
    }

    try {
      await phoneNumberService.update(phoneNumber, {
        nickname: trimmedNickname,
      });

      toast({
        title: "Nickname updated",
        status: "success",
        duration: 3000,
      });
      onUpdate();

      setIsEditing(false);
    } catch (error: any) {
      console.error("Error updating nickname:", error);
      toast({
        title: "Error updating nickname",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });

      setCurrentNickname(nickname || prettyNumber || phoneNumber);
      setIsEditing(false);
    }
  };

  return (
    <Flex align="left">
      {isEditing ? (
        <Input
          ref={inputRef}
          value={currentNickname}
          onChange={(e) => setCurrentNickname(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            } else if (e.key === "Escape") {
              setCurrentNickname(nickname || prettyNumber || phoneNumber);
              setIsEditing(false);
            }
          }}
          size="lg"
          width="auto"
          height="auto"
          fontWeight="medium"
          variant="filled"
          borderColor="transparent"
          _focus={{ borderColor: "transparent" }}
          outline="none"
        />
      ) : (
        <>
          <Heading size="md" fontWeight="medium">
            {currentNickname}
          </Heading>
          <IconButton
            aria-label="Edit nickname"
            icon={<EditIcon />}
            size="xs"
            variant="ghost"
            ml={1}
            onClick={handleEdit}
            color={isDark ? "gray.400" : "gray.500"}
            _hover={{
              color: isDark ? "white" : "gray.800",
              bg: isDark ? "gray.700" : "gray.200",
            }}
          />
        </>
      )}
    </Flex>
  );
};
